/**
 * Парсер отступов для `insertSubtree` — «скопировал список с отступами, получил поддерево»
 * (ui-spec 3.6). Лучший способ затащить в систему внешний текст, включая ответ LLM.
 */

export type OutlineDraft = {
	title: string;
	children: OutlineDraft[];
};

const BULLET_RE = /^(?:[-*+•]\s+|\d+[.)]\s+)/;

/** Таб считается за эту ширину — иначе смесь табов и пробелов даёт разные уровни. */
const TAB_WIDTH = 4;

function indentWidth(line: string): number {
	let width = 0;
	for (const ch of line) {
		if (ch === "\t") {
			width += TAB_WIDTH;
		} else if (ch === " ") {
			width += 1;
		} else {
			break;
		}
	}
	return width;
}

/**
 * Разбирает текст в дерево. Уровень определяется глубиной отступа; маркеры списка
 * (`- `, `* `, `1. `) снимаются. Пустые строки игнорируются.
 *
 * Отступы приводятся к уровням по фактически встреченным ширинам, а не делением на константу:
 * вставляют и двухпробельные списки, и четырёхпробельные, и табами — делить на фиксированное
 * число значило бы разложить половину из них в плоский список.
 */
export function parseOutlineText(text: string): OutlineDraft[] {
	const rows: { width: number; title: string }[] = [];

	for (const rawLine of text.split(/\r?\n/)) {
		if (rawLine.trim() === "") {
			continue;
		}
		const width = indentWidth(rawLine);
		const title = rawLine.slice(rawLine.length - rawLine.trimStart().length).length
			? rawLine.trimStart().replace(BULLET_RE, "").trim()
			: rawLine.replace(BULLET_RE, "").trim();
		if (title === "") {
			continue;
		}
		rows.push({ width, title });
	}

	const roots: OutlineDraft[] = [];
	// Стек открытых предков: ширина отступа + куда класть детей.
	const stack: { width: number; node: OutlineDraft }[] = [];

	for (const row of rows) {
		const node: OutlineDraft = { title: row.title, children: [] };

		while (stack.length > 0 && stack[stack.length - 1]!.width >= row.width) {
			stack.pop();
		}

		const parent = stack[stack.length - 1];
		if (parent) {
			parent.node.children.push(node);
		} else {
			roots.push(node);
		}
		stack.push({ width: row.width, node });
	}

	return roots;
}

/** Сколько нод получится — для порога подтверждения «вставить 1240 строк?» (ui-spec 3.6). */
export function countDrafts(drafts: readonly OutlineDraft[]): number {
	let n = 0;
	for (const d of drafts) {
		n += 1 + countDrafts(d.children);
	}
	return n;
}

/** Мягкий порог, за которым UI спрашивает подтверждение. Не технический предел. */
export const INSERT_SUBTREE_CONFIRM_THRESHOLD = 500;

/**
 * До скольких нод удаление остаётся отменяемым.
 *
 * Решение принимает КЛИЕНТ и до показа подтверждения: обратная команда либо кладётся в стек
 * сразу, либо не кладётся вовсе — переписать её задним числом по ответу сервера невозможно,
 * а «отмена, которая ничего не отменит» хуже честного «отменить будет нельзя».
 *
 * Тот же порог ограничивает снимок, который едет в очереди и лежит в IndexedDB.
 */
export const DELETE_UNDO_LIMIT = 500;

/**
 * Сколько ВСЕГО строк разрешено увезти в снимке отмены — нод, вплетений, ссылок и выполнений
 * вместе.
 *
 * Считать одни ноды значит мерить не то: у ноды может быть сколько угодно выполнений
 * (у ежедневной привычки — по одному на день) и сколько угодно бэклинков из чужих нод,
 * а в снимок попадают рёбра по ОБЕИМ сторонам. «Удаление одной ноды» способно набрать
 * тысячи строк, и они лягут в очередь и в IndexedDB.
 *
 * Арифметика потолка: стек держит 100 записей, строка снимка — сотни байт, то есть худший
 * случай это десятки мегабайт на вкладку при заведомо неправдоподобном сценарии (сто
 * предельных удалений подряд без перезагрузки). Больно станет — режется этим числом,
 * а не новым механизмом.
 */
export const DELETE_UNDO_ROW_LIMIT = 5_000;

/**
 * И предел по РАЗМЕРУ снимка — длина его JSON.
 *
 * Строк тоже недостаточно: у `title`, `body` и `tags` верхней длины нет, поэтому одна нода
 * с мегабайтной заметкой проходит оба предыдущих порога и уезжает в очередь и в IndexedDB
 * целиком. Сто записей стека по этому потолку — сотня мегабайт на вкладку в сценарии «сто
 * предельных удалений подряд без перезагрузки»; реальные снимки на три-четыре порядка меньше.
 */
export const DELETE_UNDO_SIZE_LIMIT = 1_000_000;
